import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/services/api';
import { useAuth } from '@/hooks/useAuth';
import toast from 'react-hot-toast';
import {
  FileText,
  Bug,
  MessageSquare,
  ThumbsUp,
  ArrowLeft,
  Clock,
  User,
  Tag,
  ExternalLink,
  Edit,
} from 'lucide-react';
import { clsx } from 'clsx';
import type { ItemType, ItemStatus, ItemPriority } from '@/types';

const typeConfig: Record<ItemType, { icon: typeof FileText; label: string; color: string; bgColor: string }> = {
  requirement: { icon: FileText, label: 'Feature Request', color: 'text-blue-600 dark:text-blue-400', bgColor: 'bg-blue-100 dark:bg-blue-900/50' },
  feedback: { icon: MessageSquare, label: 'Feedback', color: 'text-purple-600 dark:text-purple-400', bgColor: 'bg-purple-100 dark:bg-purple-900/50' },
  bug: { icon: Bug, label: 'Bug', color: 'text-red-600 dark:text-red-400', bgColor: 'bg-red-100 dark:bg-red-900/50' },
};

const statusConfig: Record<ItemStatus, { label: string; color: string; bgColor: string }> = {
  new: { label: 'New', color: 'text-blue-600 dark:text-blue-400', bgColor: 'bg-blue-100 dark:bg-blue-900/50' },
  triaged: { label: 'Triaged', color: 'text-indigo-600 dark:text-indigo-400', bgColor: 'bg-indigo-100 dark:bg-indigo-900/50' },
  planned: { label: 'Planned', color: 'text-cyan-600 dark:text-cyan-400', bgColor: 'bg-cyan-100 dark:bg-cyan-900/50' },
  in_progress: { label: 'In Progress', color: 'text-yellow-600 dark:text-yellow-400', bgColor: 'bg-yellow-100 dark:bg-yellow-900/50' },
  review: { label: 'In Review', color: 'text-orange-600 dark:text-orange-400', bgColor: 'bg-orange-100 dark:bg-orange-900/50' },
  completed: { label: 'Completed', color: 'text-green-600 dark:text-green-400', bgColor: 'bg-green-100 dark:bg-green-900/50' },
  rejected: { label: 'Rejected', color: 'text-gray-600 dark:text-gray-400', bgColor: 'bg-gray-100 dark:bg-gray-700' },
  duplicate: { label: 'Duplicate', color: 'text-gray-600 dark:text-gray-400', bgColor: 'bg-gray-100 dark:bg-gray-700' },
};

const priorityConfig: Record<ItemPriority, { label: string; color: string }> = {
  critical: { label: 'Critical', color: 'text-red-600 dark:text-red-400' },
  high: { label: 'High', color: 'text-orange-600 dark:text-orange-400' },
  medium: { label: 'Medium', color: 'text-yellow-600 dark:text-yellow-400' },
  low: { label: 'Low', color: 'text-gray-600 dark:text-gray-400' },
};

export function ItemDetail() {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [newComment, setNewComment] = useState('');

  const { data: item, isLoading } = useQuery({
    queryKey: ['item', id],
    queryFn: () => apiClient.getItem(id!),
    enabled: !!id,
  });

  const { data: comments, isLoading: commentsLoading } = useQuery({
    queryKey: ['item-comments', id],
    queryFn: () => apiClient.getComments(id!),
    enabled: !!id,
  });

  const { data: hasVoted } = useQuery({
    queryKey: ['item-vote', id],
    queryFn: () => apiClient.hasVoted(id!),
    enabled: !!id,
  });

  const voteMutation = useMutation({
    mutationFn: () => hasVoted ? apiClient.removeVote(id!) : apiClient.voteForItem(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['item', id] });
      queryClient.invalidateQueries({ queryKey: ['item-vote', id] });
      toast.success(hasVoted ? 'Vote removed' : 'Vote added');
    },
    onError: () => {
      toast.error('Failed to update vote');
    },
  });

  const commentMutation = useMutation({
    mutationFn: (content: string) => apiClient.createComment(id!, { content }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['item-comments', id] });
      queryClient.invalidateQueries({ queryKey: ['item', id] });
      setNewComment('');
      toast.success('Comment added');
    },
    onError: () => {
      toast.error('Failed to add comment');
    },
  });

  const handleSubmitComment = (e: React.FormEvent) => {
    e.preventDefault();
    if (newComment.trim()) {
      commentMutation.mutate(newComment.trim());
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!item) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500 dark:text-gray-400">Item not found</p>
        <Link to="/items" className="mt-4 text-primary-600 dark:text-primary-400 hover:underline">
          Back to items
        </Link>
      </div>
    );
  }

  const typeConf = typeConfig[item.type];
  const statusConf = statusConfig[item.status];
  const priorityConf = priorityConfig[item.priority];
  const TypeIcon = typeConf.icon;
  const canEdit = user?.is_admin || item.created_by === user?.id;

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Back link */}
      <Link
        to="/items"
        className="inline-flex items-center text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
      >
        <ArrowLeft className="mr-1 h-4 w-4" />
        Back to items
      </Link>

      {/* Header */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-start justify-between">
          <div className="flex items-start space-x-4">
            <div className={clsx('p-3 rounded-lg', typeConf.bgColor)}>
              <TypeIcon className={clsx('h-6 w-6', typeConf.color)} />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                {item.title}
              </h1>
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <span className={clsx('px-2 py-1 rounded text-xs font-medium', typeConf.bgColor, typeConf.color)}>
                  {typeConf.label}
                </span>
                <span className={clsx('px-2 py-1 rounded text-xs font-medium', statusConf.bgColor, statusConf.color)}>
                  {statusConf.label}
                </span>
                <span className={clsx('text-xs font-medium', priorityConf.color)}>
                  {priorityConf.label} Priority
                </span>
              </div>
            </div>
          </div>

          {/* Vote button */}
          <button
            onClick={() => voteMutation.mutate()}
            disabled={voteMutation.isPending}
            className={clsx(
              'flex items-center px-4 py-2 rounded-lg border transition-colors',
              hasVoted
                ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400'
                : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500'
            )}
          >
            <ThumbsUp className={clsx('h-5 w-5 mr-2', hasVoted && 'fill-current')} />
            <span className="font-medium">{item.vote_count}</span>
          </button>
        </div>

        {/* Meta info */}
        <div className="mt-6 flex flex-wrap gap-4 text-sm text-gray-500 dark:text-gray-400">
          <div className="flex items-center">
            <User className="h-4 w-4 mr-1" />
            Created by {item.created_by_name}
          </div>
          <div className="flex items-center">
            <Clock className="h-4 w-4 mr-1" />
            {new Date(item.created_at).toLocaleDateString()}
          </div>
          {item.assigned_to_name && (
            <div className="flex items-center">
              <User className="h-4 w-4 mr-1" />
              Assigned to {item.assigned_to_name}
            </div>
          )}
        </div>

        {/* Tags */}
        {item.tags.length > 0 && (
          <div className="mt-4 flex flex-wrap gap-2">
            {item.tags.map((tag) => (
              <span
                key={tag}
                className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300"
              >
                <Tag className="h-3 w-3 mr-1" />
                {tag}
              </span>
            ))}
          </div>
        )}

        {/* External link */}
        {item.external_url && (
          <div className="mt-4">
            <a
              href={item.external_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center text-sm text-primary-600 dark:text-primary-400 hover:underline"
            >
              <ExternalLink className="h-4 w-4 mr-1" />
              {item.external_id || 'External Link'}
            </a>
          </div>
        )}

        {/* Description */}
        <div className="mt-6 prose dark:prose-invert max-w-none">
          <div className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
            {item.description}
          </div>
        </div>

        {/* Actions */}
        {canEdit && (
          <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700 flex gap-2">
            <Link
              to={`/items/${item.id}/edit`}
              className="inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              <Edit className="h-4 w-4 mr-2" />
              Edit
            </Link>
          </div>
        )}
      </div>

      {/* Comments */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            Comments ({comments?.length || 0})
          </h2>
        </div>

        <div className="divide-y divide-gray-200 dark:divide-gray-700">
          {commentsLoading ? (
            <div className="p-6 text-center text-gray-500 dark:text-gray-400">Loading...</div>
          ) : comments?.length === 0 ? (
            <div className="p-6 text-center text-gray-500 dark:text-gray-400">No comments yet</div>
          ) : (
            comments?.map((comment) => (
              <div key={comment.id} className="px-6 py-4">
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0 h-8 w-8 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
                    <User className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-gray-900 dark:text-white">
                        {comment.user_name}
                      </span>
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        {new Date(comment.created_at).toLocaleString()}
                      </span>
                      {comment.is_internal && (
                        <span className="px-2 py-0.5 text-xs font-medium bg-yellow-100 dark:bg-yellow-900/50 text-yellow-700 dark:text-yellow-300 rounded">
                          Internal
                        </span>
                      )}
                    </div>
                    <p className="mt-1 text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                      {comment.content}
                    </p>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Add comment form */}
        <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700">
          <form onSubmit={handleSubmitComment}>
            <textarea
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              placeholder="Add a comment..."
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
            />
            <div className="mt-2 flex justify-end">
              <button
                type="submit"
                disabled={!newComment.trim() || commentMutation.isPending}
                className="px-4 py-2 bg-primary-600 text-white rounded-md text-sm font-medium hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {commentMutation.isPending ? 'Posting...' : 'Post Comment'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
